import React, { useCallback, useEffect, useState } from "react";
import moment from "moment";
import { ICourseSummary } from "@eagerdog/interfaces";
import { type ColDef, themeQuartz } from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";

import Button from "src/components/Button/Button";
import ClubCalendar from "src/components/ClubCalendar/ClubCalendar";
import { ClubCalendarEvent } from "src/hooks/useClubCalendarData";
import { ActionsRenderer } from "./ActionsRenderer";

import styles from "./CourseSchedule.module.scss";

interface IProps {
    course: ICourseSummary | null;
    onFilterDogsBySession(session: number): void;
    isAdmin?: boolean;
    onAddSession?(): void;
}

interface IRow {
  title: string,
  slots_left: string,
  date: string,
  time: string,
  instructors: string,
  actions: any
}

const buildCalendarEvents = (course: ICourseSummary): ClubCalendarEvent[] => {
    const total = course.schedule.length;
    return course.schedule.map((session, idx) => ({
        id: session._id || String(idx),
        title: course.title,
        start: new Date(session.start_date).toISOString(),
        end: new Date(session.end_date).toISOString(),
        allDay: false,
            extendedProps: {
                type: "course" as const,
                handle: (course as any).handle || "",
                sessionIndex: idx + 1,
                sessionTotal: total,
                instructor: session.instructors?.[0]
                    ? `${session.instructors[0].first_name} ${session.instructors[0].last_name}`
                    : undefined,
                sourceId: course._id,
                enrolled: session.regular_entries ?? 0,
                capacity: session.regular_capacity ?? 0,
            },
    }));
};

const CourseSchedule: React.FC<IProps> = (props) => {
    const { course } = props;
    const [rowData, setRowData] = useState<IRow[]>([]);
    const [view, setView] = useState<"list" | "calendar">("list");

    const [calendarEvents, setCalendarEvents] = useState<ClubCalendarEvent[]>(
        () => (course ? buildCalendarEvents(course) : [])
    );

    // Keep calendarEvents in sync when the course prop is refreshed from the server
    useEffect(() => {
        if (course) setCalendarEvents(buildCalendarEvents(course));
    }, [course]);

    const handleEventsChanged = useCallback((updated: ClubCalendarEvent[]) => {
        setCalendarEvents(updated);
    }, []);

    const theme = themeQuartz.withParams({
        fontFamily: "Roboto",
        headerFontFamily: "Roboto",
        cellFontFamily: "Roboto"
    });

    let colDefs:ColDef<IRow>[] = props.isAdmin ? [
        { field: "title", minWidth: 120, filter: "agTextColumnFilter" },
        { field: "slots_left", headerName: "Enrolled", minWidth:120 },
        { field: "date", minWidth: 180, filter: "agTextColumnFilter" },
        { field: "time", minWidth: 120, filter:true },
        { field: "instructors", minWidth: 150, filter:true },
        {
            headerName: "",
            field: "actions",
            maxWidth: 50,
            minWidth: 50,
            cellRenderer: ActionsRenderer,
            pinned: "right",
            cellRendererParams: {
                filterDogsBySession: (session: number) => {
                    props.onFilterDogsBySession(session);
                }
            },
            resizable: false,
            cellStyle: {
                "overflow": "visible",
                "zIndex": "2"
            }
        }
    ] : [
        { field: "title", minWidth: 120, filter: "agTextColumnFilter" },
        { field: "date", minWidth: 180, filter: "agTextColumnFilter" },
        { field: "time", minWidth: 120, filter:true },
        { field: "instructors", minWidth: 150, filter:true }
    ];
    

    useEffect(() => {
        let rows: IRow[] = [];

        if (course) {
            for (let s in course.schedule) {
                const capacity = course.schedule[s].regular_capacity || 0;
                const entries = course.schedule[s].regular_entries || 0;
                
                rows.push({
                    title: "Session " + (parseInt(s) + 1),
                    slots_left: `${entries}/${capacity}`,
                    date: moment(course.schedule[s].start_date).format("MMMM Do YYYY"),
                    time: moment(course.schedule[s].start_date).format("h:mmA"),
                    instructors: Array.isArray(course.schedule[s].instructors) && course.schedule[s].instructors.length > 0
                        ? course.schedule[s].instructors.map((instructor) => instructor.first_name + " " + instructor.last_name).join(", ")
                        : "None",
                    actions: {
                        session: course.schedule[s]
                    }
                });
            }

            setRowData(rows);
        }
    }, [course]);

    return (
        <div className={styles.CourseSchedule}>
            <div className={styles.toolbar}>
                <div className={styles.viewToggle}>
                    <button
                        className={`${styles.toggleBtn} ${view === "list" ? styles.toggleActive : ""}`}
                        onClick={() => setView("list")}
                    >List</button>
                    <button
                        className={`${styles.toggleBtn} ${view === "calendar" ? styles.toggleActive : ""}`}
                        onClick={() => setView("calendar")}
                    >Calendar</button>
                </div>
                {props.isAdmin && props.onAddSession && (
                    <Button onClick={props.onAddSession}>Add Session</Button>
                )}
            </div>

            {view === "list" ? (
                <AgGridReact
                    rowData={rowData}
                    columnDefs={colDefs}
                    domLayout="normal"
                    gridOptions={{
                        defaultColDef: { flex: 1, minWidth: 100, resizable: true },
                        animateRows: true,
                        rowHeight: 48,
                        headerHeight: 48,
                        overlayNoRowsTemplate: '<span>No sessions available</span>'
                    }}
                />
            ) : (
                <div className={styles.calendarWrap}>
                    <ClubCalendar
                        clubId={course?.club_id?.toString() || ""}
                        isAdmin={props.isAdmin}
                        initialEvents={calendarEvents}
                        onEventsChanged={props.isAdmin ? handleEventsChanged : undefined}
                    />
                </div>
            )}
        </div>
    );
};

export default CourseSchedule;
